/**
 * A prefixed, zero-padded sequential id for a new array-section entry — the
 * highest existing number across current entries, plus one. Not the last
 * entry positionally: removing and re-adding an entry, or reordering the
 * list, must never resurrect a retired number.
 *
 * Page-agnostic: the prefix and digit width are the caller's own scheme
 * (follow-ups' `FUP001`, or another page's own format) — see
 * `ConfigSchema.GenerateEntryId`.
 */
import type { SequentialIdScheme } from "@/types/clinic-configuration";
import {
  CONFIG_ENTRY_TITLE_FIELD,
  SEQUENTIAL_ID_DIGITS,
} from "@/constants/clinic-configuration.constants";
import { isString } from "@/lib/utils/typeof";

/**
 * The same rule for any list of ids, such as a resource's identifier
 * values. `digits` is a minimum: past `999` comes `1000`, and `1000` still
 * counts as the highest, so the next id is never handed out twice.
 */
export const getNextSequentialId = (
  ids: string[],
  { prefix, digits = SEQUENTIAL_ID_DIGITS }: SequentialIdScheme
): string => {
  const pattern = new RegExp(`^${prefix}(\\d{${digits},})$`);

  const highestSequence = ids.reduce((highest, id) => {
    const match = id.match(pattern);

    return match ? Math.max(highest, Number(match[1])) : highest;
  }, 0);

  return `${prefix}${String(highestSequence + 1).padStart(digits, "0")}`;
};

export const generateSequentialEntryId = (
  entries: Record<string, unknown>[],
  scheme: SequentialIdScheme
): string =>
  getNextSequentialId(
    entries.map((entry) => entry[CONFIG_ENTRY_TITLE_FIELD]).filter(isString),
    scheme
  );
